import { Form as AntForm } from 'antd';
import { Controller, useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';
import type { ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';

interface FormFieldProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  extra?: ReactNode;
  /** Use 'checked' for Checkbox / Switch — defaults to 'value'. */
  valueProp?: 'value' | 'checked';
  children: ReactElement<{ value?: unknown; onChange?: (...args: unknown[]) => void }>;
}

export function FormField<TValues extends FieldValues>({
  name,
  label,
  required,
  help,
  extra,
  valueProp = 'value',
  children,
}: FormFieldProps<TValues>) {
  const { control } = useFormContext<TValues>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { value, ...rest } = field;
        const childProps =
          valueProp === 'checked'
            ? { ...rest, checked: !!value, id: field.name }
            : { ...field, id: field.name };
        return (
          <AntForm.Item
            label={label}
            required={required}
            validateStatus={fieldState.error ? 'error' : undefined}
            help={fieldState.error?.message ?? help}
            extra={extra}
            htmlFor={field.name}
          >
            {isValidElement(children)
              ? cloneElement(children, childProps as Record<string, unknown>)
              : children}
          </AntForm.Item>
        );
      }}
    />
  );
}
